import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ChevronDown, Menu, X, LogOut, LayoutDashboard, Heart } from 'lucide-react';
import { navigation } from '../mock';
import { useAuth } from '../context/AuthContext';
import NotificationBell from './NotificationBell';
import { useCart } from '../context/CartContext';
import LanguageSwitcher from './LanguageSwitcher';
import oshoLogo from '../assets/osho-logo.png';

const Logo = () => (
  <Link to="/" className="flex items-center" aria-label="Osho Universal home">
    <img src={oshoLogo} alt="Osho Universal" className="h-12 w-auto" />
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null);
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  return (
    <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur-sm border-b border-rose/40">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Logo />

        <nav className="hidden xl:flex items-center gap-5">
          {navigation.map((item) => (
            item.children ? (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => setMenu(item.name)}
                onMouseLeave={() => setMenu(null)}
                data-testid={`nav-dropdown-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <button
                  className="text-[15px] text-burgundy/90 hover:text-mahogany transition-colors inline-flex items-center gap-1"
                >
                  {item.name}
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menu === item.name ? 'rotate-180' : ''}`} />
                </button>
                {menu === item.name && (
                  <div className="absolute left-0 top-full pt-3 z-50">
                    <div className="min-w-[280px] rounded-2xl bg-cream border border-rose/40 shadow-xl p-2">
                      {item.children.map((c) => (
                        <Link
                          key={c.name}
                          to={c.to}
                          onClick={() => setMenu(null)}
                          data-testid={`nav-link-${c.to.replace(/\//g, '')}`}
                          className="block px-4 py-2.5 rounded-xl text-[15px] text-burgundy/90 hover:bg-rose-soft/50 hover:text-mahogany transition-colors"
                        >
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              item.to ? (
                <Link
                  key={item.name}
                  to={item.to}
                  data-testid={`nav-top-${item.to.replace(/\//g, '') || 'home'}`}
                  className="text-[15px] text-burgundy/90 hover:text-mahogany transition-colors relative group"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-mahogany group-hover:w-full transition-[width] duration-300" />
                </Link>
              ) : (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-[15px] text-burgundy/90 hover:text-mahogany transition-colors relative group inline-flex items-center gap-1"
                >
                  {item.name}
                  {item.hasDropdown && <ChevronDown className="w-3.5 h-3.5" />}
                  <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-mahogany group-hover:w-full transition-[width] duration-300" />
                </a>
              )
            )
          ))}
        </nav>

        <div className="flex items-center gap-2" data-no-translate>
          <LanguageSwitcher />
          <Link to="/donate" data-testid="nav-donate" className="hidden md:inline-flex items-center gap-1.5 rounded-full bg-mahogany text-cream text-[14px] px-4 py-2 hover:bg-burgundy transition-colors">
            <Heart className="w-4 h-4" /> Donate
          </Link>
          <Link to="/cart" className="relative flex items-center gap-1.5 h-10 pl-3 pr-3.5 rounded-full hover:bg-rose-soft/40 transition-colors text-[15px] text-burgundy/90" aria-label="Shop cart" data-testid="header-cart">
            <ShoppingBag className="w-4.5 h-4.5 text-burgundy" />
            <span className="hidden lg:inline">Shop</span>
            {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-mahogany text-cream text-[10px] flex items-center justify-center" data-testid="cart-count">{cartCount}</span>}
          </Link>
          {user ? (
            <>
              <NotificationBell />
              <Link to="/dashboard" className="pill-btn-outline hidden md:inline-flex">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              <button onClick={logout} className="hidden md:flex w-10 h-10 items-center justify-center rounded-full hover:bg-rose-soft/40 transition-colors" aria-label="Logout" title="Sign out">
                <LogOut className="w-4 h-4 text-burgundy" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline-flex text-[15px] text-burgundy/90 hover:text-mahogany px-2 py-2">Sign in</Link>
              <Link to="/signup" className="pill-btn-outline hidden md:inline-flex">Join</Link>
            </>
          )}
          <button className="xl:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-rose-soft/40" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-5 h-5 text-burgundy" /> : <Menu className="w-5 h-5 text-burgundy" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-cream border-t border-rose/40 px-6 py-4">
          <div className="flex flex-col gap-3">
            {navigation.map((item) => (
              <React.Fragment key={item.name}>
                {item.to ? (
                  <Link to={item.to} className="text-burgundy py-1.5 border-b border-rose/30" onClick={() => setOpen(false)}>
                    {item.name}
                  </Link>
                ) : (
                  <a href={item.href} className="text-burgundy py-1.5 border-b border-rose/30" onClick={() => setOpen(false)}>
                    {item.name}
                  </a>
                )}
                {item.children && item.children.map((c) => (
                  <Link key={c.name} to={c.to} onClick={() => setOpen(false)}
                    className="text-burgundy/80 text-sm py-1.5 pl-4 border-b border-rose/20" data-testid={`nav-link-mobile-${c.to.replace(/\//g, '')}`}>
                    ↳ {c.name}
                  </Link>
                ))}
              </React.Fragment>
            ))}
            <Link to="/shop" className="text-burgundy py-1.5 border-b border-rose/30" onClick={() => setOpen(false)} data-testid="nav-shop-mobile">Shop</Link>
            <Link to="/donate" className="text-burgundy py-1.5 border-b border-rose/30" onClick={() => setOpen(false)} data-testid="nav-donate-mobile">Donate</Link>
            <div className="py-1.5 border-b border-rose/30"><LanguageSwitcher compact /></div>
            {user ? (
              <>
                <Link to="/dashboard" className="pill-btn mt-2 justify-center">Dashboard</Link>
                <button onClick={logout} className="pill-btn-outline mt-2 justify-center">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="pill-btn-outline mt-2 justify-center">Sign in</Link>
                <Link to="/signup" className="pill-btn mt-2 justify-center">Create account</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
